<template lang="pug">
v-card
  v-container
    v-row.pa-4.pa-sm-8
      v-col.d-flex.flex-sm-column-reverse.justify-space-between.align-stretch.order-sm-1.pt-sm-0.pr-sm-0(cols="12", sm="6")
        ConfettiBag.align-self-sm-start(:width="graphicWidth")
        v-btn.align-self-start.align-self-sm-end(icon, @click="close")
          v-icon(color="textColor") {{ closeIcon }}
      v-col.order-sm-0(cols="12", sm="6")
        Header(:text="modalContent.headerText", :level="5")
        Subtitle(:text="modalContent.subtitleText")
        TextBody(:text="modalContent.bodyText")
        GuestAmountSelection(
          v-model="guests",
        )
        v-btn(
          color="primary",
          @click="onSubmit",
          :disabled="disableSubmit"
        ) Gästezahl speichern
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue"
import { EVENT_STATUS } from "constants/constants"
import { createNamespacedHelpers } from "vuex"

const { mapState } = createNamespacedHelpers("event")
import Header from "../UI/Header.vue"
import Subtitle from "../UI/Subtitle.vue"
import TextBody from "../UI/TextBody.vue"
import ConfettiBag from "../graphics/ConfettiBag.vue"
import { mdiClose } from "@mdi/js"
import { mdiMinus } from "@mdi/js"
import { mdiPlus } from "@mdi/js"
import { Event as IEvent } from "suus-api"
import GuestAmountSelection from "components/Event/GuestAmountSelection.vue"
import { useOwnUserAttendanceContext } from "app/modules/events/use-own-user-attendance-context.concern"
import { useRSVPContextOptional } from "app/modules/events/rsvp-context/use-rsvp-context.concern"

export default defineComponent({
  name: "GuestModal",
  props: { guest_count: { type: Number, required: true } },
  components: {
    GuestAmountSelection,
    Header,
    TextBody,
    Subtitle,
    ConfettiBag,
  },
  data() {
    return {
      showModal: false,
      closeIcon: mdiClose,
      minusIcon: mdiMinus,
      plusIcon: mdiPlus,
      isMobile: this.$vuetify.breakpoint.mobile,
      disableSubmit: false,
      validationPattern: /^[0-9]{1,2}$/,
    }
  },
  methods: {
    add() {
      this.guests = this.guests + 1
    },
    subtract() {
      if (this.guests > 0) {
        this.guests = this.guests - 1
      }
    },
    close() {
      this.$emit("close", this.guests)
    },
    onSubmit() {
      this.close()
      this.setEventAttendStatusDirectly(EVENT_STATUS.FINISHED_REGISTRATION)
      if (this.$route.name === "Event") {
        this.attend(this.guests)
      }
      if (this.$route.name === "Attend") {
        this.attendFromDigest(this.guests)
      }
    },
  },
  computed: {
    rules(): object {
      return {
        required: (value) => {
          if (value === 0 || !!value) {
            return true
          }
          return this.$i18n.t("components.guest_modal.rules.rule1")
        },
        guests: (value) => {
          if (value > 20) {
            return this.$i18n.t("components.guest_modal.rules.rule2")
          }
          return (
            this.validationPattern.test(value) ||
            this.$i18n.t("components.guest_modal.rules.rule3")
          )
        },
      }
    },
    modalContent(): {
      headerText: string
      subtitleText: string
      bodyText: string
    } {
      return {
        headerText: this.$i18n
          .t("components.guest_modal.headerText")
          .toString(),
        subtitleText: this.$i18n
          .t("components.guest_modal.subtitleText")
          .toString(),
        bodyText: this.$i18n.t("components.guest_modal.bodyText").toString(),
      }
    },
    graphicWidth(): string {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return "180"
        default:
          return "250"
      }
    },
    ...mapState({
      currentEvent: (state: any): IEvent => state.currentEvent.data,
    }),
  },
  watch: {
    guests(val: number) {
      if (
        val === undefined ||
        val === null ||
        val > 20 ||
        !this.validationPattern.test(val.toString())
      ) {
        this.disableSubmit = true
        return
      }
      this.disableSubmit = false
    },
  },
  setup(props) {
    const {
      setEventAttendStatusDirectly,
      attend: _attend,
      attendFromDigest,
      ownUserGuestsCount,
      updateAttendance,
      ownUserId,
    } = useOwnUserAttendanceContext()

    const guests = ref(props.guest_count || 0)
    watch(
      () => ownUserGuestsCount.value,
      (val) => {
        guests.value = val
      },
      { immediate: true }
    )

    const rsvpContextOptional = useRSVPContextOptional()

    const attend = (guests: number) => {
      if (rsvpContextOptional) {
        updateAttendance(() =>
          rsvpContextOptional.updateRSVP(
            parseInt(ownUserId.value),
            "attend",
            guests
          )
        )
      } else {
        _attend(guests)
      }
    }

    return {
      setEventAttendStatusDirectly,
      attend,
      attendFromDigest,
      guests,
    }
  },
})
</script>

<style lang="sass">
$text-color: rgba(66, 66, 66, 1)
%font-stack
  font-family: system-ui !important
  color: $text-color

.suus__counter
  width: 180px !important

  .v-input__control > .v-input__slot
    padding: 0 !important

    .v-input__prepend-inner
      margin: 0 !important

    .v-input__append-inner
      margin: 0 !important

    .v-text-field__slot
      input
        @extend %font-stack
        text-align: center !important
        font-weight: 500
        font-size: 24px

  &__label
    @extend %font-stack
    font-weight: 700
    text-align: center
</style>
